/**
 * HelpCenterTab — Embedded Help Center within the Support Hub
 *
 * Provides quick tips, FAQ access, and links to the full HelpSystem drawer.
 * Fetches content from Supabase KB — no hardcoded data.
 */

import React, { useState, useMemo } from 'react';
import { HelpLinkRenderer } from '../../../components/help/HelpLinkRenderer';
import {
    Box,
    Typography,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Chip,
    useTheme,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import {
    QuestionAnswer as FaqIcon,
    ExpandMore as ExpandMoreIcon,
    MenuBook as KBIcon,
    Article as ArticleIcon,
    OpenInNew as OpenIcon,
} from '@mui/icons-material';
import { useHelpSearch } from '../../../hooks/useHelpContext';
import { useSupportHub } from '../SupportHubContext';
import { useHelpPageContext } from '../../help-chat/HelpPageContext';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useLocation } from 'react-router-dom';
import { getKBArticles } from '../../../services/knowledgeBase.service';
import { useQuery } from '@tanstack/react-query';
import type { KBArticle } from '../../../types/knowledgeBase.types';

// Page-context to help context key mapping
const PAGE_HELP_MAP: Record<string, string> = {
    '/dashboard': 'user-dashboard',
    '/documents': 'documents-list',
    '/jobs': 'jobs-management',
    '/compliance': 'documents-compliance',
    '/profile': 'profile-settings',
    '/messages': 'messages-help',
    '/customers': 'contact-customer',
};

const HelpCenterTab: React.FC = () => {
    const theme = useTheme();
    const { searchQuery } = useSupportHub();
    const { currentPageId } = useHelpPageContext();
    const { isEnabled: isHelpChatV2 } = useFeatureFlag('help_chat.v2.enabled');
    const location = useLocation();
    const [helpDrawerOpen, setHelpDrawerOpen] = useState(false);

    // Determine context: with help-chat v2 on, the active registry page context wins over the
    // path mapping; with it off (or still loading) this is the pre-v2 path mapping only.
    const currentPath = location.pathname;
    const contextKey = Object.keys(PAGE_HELP_MAP).find((key) => currentPath.startsWith(key));
    const pathFallback = contextKey ? PAGE_HELP_MAP[contextKey] : 'documents-upload';
    const helpContextId = (isHelpChatV2 ? currentPageId : null) ?? pathFallback;

    // Fetch general KB articles for browsing (when not searching)
    const { data: generalArticles } = useQuery({
        queryKey: ['help-center-tab-articles'],
        queryFn: async () => {
            const result = await getKBArticles({
                is_published: true,
                sort_by: 'views',
                sort_order: 'desc',
                limit: 8,
            });
            return result.data ?? [];
        },
        staleTime: 5 * 60 * 1000,
    });

    // Search content from Supabase
    const { data: searchResults } = useHelpSearch(searchQuery);

    // Determine which content to display
    const filteredContent = useMemo(() => {
        if (searchQuery.trim() && searchResults) {
            return searchResults;
        }
        return generalArticles ?? [];
    }, [searchQuery, searchResults, generalArticles]);

    // Lazy-load the HelpSystem drawer
    const HelpSystemDrawer = React.useMemo(() => {
        if (!helpDrawerOpen) return null;
        const HelpSystem = React.lazy(() => import('../../../components/HelpSystem').then(m => ({ default: m.HelpSystem })));
        return (
            <React.Suspense fallback={null}>
                <HelpSystem
                    open={helpDrawerOpen}
                    onClose={() => setHelpDrawerOpen(false)}
                    context={helpContextId}
                />
            </React.Suspense>
        );
    }, [helpDrawerOpen, helpContextId]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* ── Open full help drawer ── */}
            <ListItemButton
                onClick={() => setHelpDrawerOpen(true)}
                sx={{
                    borderRadius: '10px',
                    border: `1px solid ${theme.palette.divider}`,
                    py: 1.25,
                }}
            >
                <ListItemIcon sx={{ minWidth: 36 }}>
                    <KBIcon color="secondary" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                    disableTypography
                    primary={
                        <Typography component="span" display="block" variant="body2" fontWeight={600}>
                            Open Full Help System
                        </Typography>
                    }
                    secondary={
                        <Typography component="span" display="block" variant="body2" color="text.secondary">
                            Detailed guides, troubleshooting & tips
                        </Typography>
                    }
                />
                <OpenIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            </ListItemButton>

            <Divider />

            {/* ── Quick FAQs ── */}
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                    <FaqIcon sx={{ fontSize: '1rem', color: 'secondary.main' }} />
                    <Typography variant="caption" fontWeight={600} color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>
                        {searchQuery.trim() ? `Results (${filteredContent.length})` : 'Quick Answers'}
                    </Typography>
                </Box>

                {filteredContent.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                        No results found. Try different keywords.
                    </Typography>
                ) : (
                    filteredContent.map((item: KBArticle, index: number) => (
                        <Accordion
                            key={`${item.id}-${index}`}
                            disableGutters
                            elevation={0}
                            sx={{
                                '&:before': { display: 'none' },
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: '8px !important',
                                mb: 0.75,
                                overflow: 'hidden',
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon fontSize="small" />}
                                sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.75 } }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <ArticleIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                                    <Typography variant="body2" fontSize="0.8rem" fontWeight={500}>
                                        {item.title}
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ pt: 0 }}>
                                <Typography variant="body2" fontSize="0.78rem" color="text.secondary" lineHeight={1.5} component="div">
                                    <HelpLinkRenderer text={item.excerpt ?? item.content.slice(0, 300)} />
                                </Typography>
                                {item.tags && (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                                        {item.tags.map((tag: string) => (
                                            <Chip
                                                key={tag}
                                                label={tag}
                                                size="small"
                                                sx={{ fontSize: '0.65rem', height: 20 }}
                                            />
                                        ))}
                                    </Box>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    ))
                )}
            </Box>

            {/* Full HelpSystem drawer (rendered outside the panel) */}
            {HelpSystemDrawer}
        </Box>
    );
};

export default HelpCenterTab;
